import { FC, useCallback, useMemo } from 'react';
import { Accordion, AccordionItem, Checkbox } from '@carbon/react';
import { DynamicDateFilter } from 'components/dynamic-date-filter';
import type { EntityType } from 'lib/entities';
import type { EntitySearchParams, DateRange } from './types';
import styles from './entity-dashboard.module.scss';

interface EntityFiltersProps {
  payload: EntitySearchParams | undefined;
  onCreatedAtChange: (createdAt?: DateRange) => void;
  onUpdatedAtChange: (updatedAt?: DateRange) => void;
  onEntityTypesChange: (types: EntityType[]) => void;
  // The Tags section renders only when this is passed.
  onOperatorOnlyChange?: (checked: boolean) => void;
  onEntityTypeFilterChange?: (primary: boolean, aliases: boolean) => void;
  onDeduplicatedChange?: (includeDeduplicated: boolean) => void;
  disableEntityTypeFilter?: boolean;
  // Disables the Deduplicated checkbox (e.g. the dedupe modal, where you can never
  // select an already-deduplicated entity).
  disableDeduplicatedFilter?: boolean;
}

const DATE_FILTER_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'updatedAt', label: 'Updated Date' },
];

export const EntityFilters: FC<EntityFiltersProps> = ({
  payload,
  onCreatedAtChange,
  onUpdatedAtChange,
  onEntityTypesChange,
  onOperatorOnlyChange,
  onEntityTypeFilterChange,
  onDeduplicatedChange,
  disableEntityTypeFilter = false,
  disableDeduplicatedFilter = false,
}) => {
  const entityTypes = payload?.entityTypes || [];
  const operatorOnly = payload?.operatorOnly ?? false;
  const includePrimary = payload?.includePrimary ?? true;
  const includeAliases = payload?.includeAliases ?? true;
  const includeDeduplicated = payload?.includeDeduplicated ?? false;

  const handleEntityTypeToggle = (type: EntityType, checked: boolean) => {
    if (checked) {
      onEntityTypesChange([...entityTypes, type]);
    } else {
      onEntityTypesChange(entityTypes.filter((t) => t !== type));
    }
  };

  const dateFilterValues = useMemo(() => {
    const values: Array<{ type: string; start?: string; end?: string }> = [];
    if (payload?.createdAt?.start || payload?.createdAt?.end) {
      values.push({
        type: 'createdAt',
        start: payload.createdAt.start,
        end: payload.createdAt.end,
      });
    }
    if (payload?.updatedAt?.start || payload?.updatedAt?.end) {
      values.push({
        type: 'updatedAt',
        start: payload.updatedAt.start,
        end: payload.updatedAt.end,
      });
    }
    return values.length > 0 ? values : undefined;
  }, [payload?.createdAt, payload?.updatedAt]);

  const handleDateFiltersChange = useCallback(
    (values?: Array<{ type: string; start?: string; end?: string }>) => {
      const createdAtValue = values?.find((v) => v.type === 'createdAt');
      const updatedAtValue = values?.find((v) => v.type === 'updatedAt');

      const newCreatedAt =
        createdAtValue?.start || createdAtValue?.end
          ? { start: createdAtValue.start, end: createdAtValue.end }
          : undefined;

      const newUpdatedAt =
        updatedAtValue?.start || updatedAtValue?.end
          ? { start: updatedAtValue.start, end: updatedAtValue.end }
          : undefined;

      const currentCreatedAt = payload?.createdAt;
      const currentUpdatedAt = payload?.updatedAt;

      if (
        newCreatedAt?.start !== currentCreatedAt?.start ||
        newCreatedAt?.end !== currentCreatedAt?.end
      ) {
        onCreatedAtChange(newCreatedAt);
      }

      if (
        newUpdatedAt?.start !== currentUpdatedAt?.start ||
        newUpdatedAt?.end !== currentUpdatedAt?.end
      ) {
        onUpdatedAtChange(newUpdatedAt);
      }
    },
    [
      payload?.createdAt,
      payload?.updatedAt,
      onCreatedAtChange,
      onUpdatedAtChange,
    ]
  );

  const handlePrimaryToggle = (checked: boolean) => {
    onEntityTypeFilterChange?.(checked, includeAliases);
  };

  const handleAliasesToggle = (checked: boolean) => {
    onEntityTypeFilterChange?.(includePrimary, checked);
  };

  return (
    <Accordion className={styles.filterAccordion}>
      <AccordionItem title="Date Filters" open={true}>
        <DynamicDateFilter
          variant="multi-select"
          values={dateFilterValues}
          onChange={handleDateFiltersChange}
          dateOptions={DATE_FILTER_OPTIONS}
        />
      </AccordionItem>

      <AccordionItem title="Entity Classification" open={true}>
        <div className={styles.checkboxGroup}>
          <Checkbox
            id="entity-type-primary"
            labelText="Primary"
            checked={includePrimary}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handlePrimaryToggle(checked)
            }
          />
          <Checkbox
            id="entity-type-aliases"
            labelText="Aliases"
            checked={includeAliases}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleAliasesToggle(checked)
            }
          />
          <Checkbox
            id="entity-classification-deduplicated"
            labelText="Deduplicated"
            checked={includeDeduplicated}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              onDeduplicatedChange?.(checked)
            }
            disabled={disableDeduplicatedFilter}
          />
        </div>
      </AccordionItem>

      <AccordionItem title="Entity Type" open={true}>
        <div className={styles.checkboxGroup}>
          <Checkbox
            id="entity-type-individual"
            labelText="Individual"
            checked={entityTypes.includes('Individual')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleEntityTypeToggle('Individual', checked)
            }
            disabled={disableEntityTypeFilter}
          />
          <Checkbox
            id="entity-type-organization"
            labelText="Organization"
            checked={entityTypes.includes('Organization')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleEntityTypeToggle('Organization', checked)
            }
            disabled={disableEntityTypeFilter}
          />
          <Checkbox
            id="entity-type-imported"
            labelText="Imported"
            checked={entityTypes.includes('Imported')}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleEntityTypeToggle('Imported', checked)
            }
            disabled={disableEntityTypeFilter}
          />
        </div>
      </AccordionItem>

      {onOperatorOnlyChange && (
        <AccordionItem title="Tags" open={true}>
          <div className={styles.checkboxGroup}>
            <Checkbox
              id="entity-operator-only"
              labelText="Operator"
              checked={operatorOnly}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                onOperatorOnlyChange(checked)
              }
            />
          </div>
        </AccordionItem>
      )}
    </Accordion>
  );
};
